import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Send,
  KeyRound,
  RefreshCw,
  Unplug,
  CheckCircle2,
  AlertCircle,
  Upload,
  Trash2,
  Film,
  PlayCircle,
  Link2,
  ShoppingCart,
  Zap,
  Gamepad2,
  Bot,
  ExternalLink,
  Users,
  MessageCircle,
  Radar as RadarIcon
} from "lucide-react";
import { Button } from "./ui/button";

// Pestaña «Telegram» (solo admin): canal propio con vídeos a horas fijas. Solo /api/admin/tgc/*.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin/tgc`;

const authHeaders = () => {
  const token = localStorage.getItem("cortoviral_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};
const opts = () => ({ headers: authHeaders(), withCredentials: true });

const STATUS = {
  planning: { label: "Preparando", style: "bg-blue-500/15 text-blue-300 border-blue-500/30", busy: true },
  queued: { label: "En cola de render", style: "bg-slate-700/40 text-slate-300 border-white/10", busy: true },
  rendering: { label: "Montando", style: "bg-blue-500/15 text-blue-300 border-blue-500/30", busy: true },
  uploading: { label: "Publicando", style: "bg-blue-500/15 text-blue-300 border-blue-500/30", busy: true },
  ready: { label: "Listo, sin publicar", style: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  published: { label: "En el canal", style: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  failed: { label: "Fallido", style: "bg-red-500/15 text-red-300 border-red-500/30" }
};
const SOURCE_ICON = { comparacesta: ShoppingCart, prisa: Zap, juegos: Gamepad2, radar: RadarIcon };
const HOURS = [8, 9, 10, 12, 14, 16, 18, 20, 21, 22];

export const TelegramChannelView = () => {
  const [status, setStatus] = useState(null);
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [videos, setVideos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ channel: "", bot_token: "" });
  const [game, setGame] = useState("");
  const [working, setWorking] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [preview, setPreview] = useState(null);
  const timer = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/account`, opts());
      setStatus(res.data);
    } catch (e) {
      setFeedback({ type: "error", text: e.response?.data?.detail || "No se pudo cargar el canal." });
    }
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/config`, opts());
      setConfig(res.data);
    } catch (e) {
      /* se reintenta en el siguiente refresco */
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/stats`, opts());
      setStats(res.data);
    } catch (e) {
      /* se reintenta en el siguiente refresco */
    }
  }, []);

  const loadVideos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/videos`, opts());
      setVideos(res.data.videos);
      setBusy(res.data.busy);
    } catch (e) {
      /* se reintenta en el siguiente refresco */
    }
  }, []);

  useEffect(() => {
    loadStatus();
    loadConfig();
    loadStats();
    loadVideos();
  }, [loadStatus, loadConfig, loadStats, loadVideos]);

  const anyBusy = busy || videos.some((v) => STATUS[v.status]?.busy);
  useEffect(() => {
    clearInterval(timer.current);
    if (anyBusy) timer.current = setInterval(() => { loadVideos(); loadStatus(); }, 5000);
    return () => clearInterval(timer.current);
  }, [anyBusy, loadVideos, loadStatus]);

  const run = async (key, fn, okText) => {
    setWorking(key);
    setFeedback(null);
    try {
      const msg = await fn();
      if (okText || msg) setFeedback({ type: "success", text: msg || okText });
    } catch (e) {
      setFeedback({ type: "error", text: e.response?.data?.detail || "Ha habido un error." });
    } finally {
      setWorking((w) => (w === key ? null : w));
    }
  };

  const connect = () => run("connect", async () => {
    const res = await axios.post(`${API}/account/connect`, form, opts());
    setStatus(res.data);
    setForm({ channel: "", bot_token: "" });
    loadStats();
  }, "Canal conectado.");

  const disconnect = () => {
    if (!window.confirm("¿Desconectar el canal? Los mensajes ya publicados se quedan allí.")) return;
    run("disconnect", async () => {
      const res = await axios.post(`${API}/account/disconnect`, {}, opts());
      setStatus(res.data);
    }, "Canal desconectado.");
  };

  const saveConfig = (patch) => run("config", async () => {
    const res = await axios.post(`${API}/config`, patch, opts());
    setConfig(res.data);
  });

  const toggleIn = (list, item) => (list.includes(item) ? list.filter((x) => x !== item) : [...list, item]);

  const createVideo = (source, mode) => run(`create-${source}-${mode || ""}`, async () => {
    await axios.post(`${API}/videos/create`, { source, mode, publish: !!status?.connected }, opts());
    setBusy(true);
    loadVideos();
  }, status?.connected
    ? "Creando el vídeo. En un par de minutos estará en el canal."
    : "Creando el vídeo. Como el canal no está conectado, se quedará aquí para verlo.");

  const publishVideo = (v) => run(`pub-${v.id}`, async () => {
    await axios.post(`${API}/videos/${v.id}/publish`, {}, opts());
    loadVideos();
    loadStatus();
  }, "Publicado en el canal.");

  // Meta no da una API oficial para los canales de WhatsApp: aquí se deja todo listo y el dueño lo publica a mano
  const prepareWhatsApp = (v) => run(`wa-${v.id}`, async () => {
    setPreview(v.id);
    const copied = await navigator.clipboard.writeText(v.whatsapp_text).then(() => true, () => false);
    const res = await axios.get(`${BACKEND_URL}${v.video_url}`, { ...opts(), responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `whatsapp-${v.id.slice(0, 8)}.mp4`;
    a.click();
    URL.revokeObjectURL(url);
    return copied
      ? "Texto copiado y vídeo descargado. Súbelo a tu canal de WhatsApp y pega el texto."
      : "Vídeo descargado. No se pudo copiar el texto: cópialo de la vista previa.";
  });

  const deleteVideo = (v) => {
    if (!window.confirm(`¿Borrar «${v.title}» de CortoViral? (Si ya está en el canal, allí se queda.)`)) return;
    run(`del-${v.id}`, async () => {
      await axios.delete(`${API}/videos/${v.id}`, opts());
      if (preview === v.id) setPreview(null);
      loadVideos();
    });
  };

  if (!status || !config) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-400 text-sm gap-2">
        <span className="flex items-center gap-2"><RefreshCw className="w-4 h-4 animate-spin" /> Cargando Telegram...</span>
        {feedback && <span className="text-red-400 text-xs">{feedback.text}</span>}
      </div>
    );
  }

  const s = config.settings;
  const gamesWithMaterial = Object.entries(config.games).filter(([, g]) => g.ok);
  const members = status.members || 0;
  const pct = Math.min(100, Math.round((members / status.goal) * 100));
  const fbStyle = feedback?.type === "success"
    ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-300"
    : feedback?.type === "info" ? "bg-blue-500/10 border border-blue-500/30 text-blue-200"
      : "bg-red-500/10 border border-red-500/30 text-red-300";
  const card = "bg-[#12141D] border border-white/10 rounded-2xl p-6 space-y-3";
  const input = "w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-500/60";
  const chip = (on) => `px-2.5 py-1.5 rounded-lg text-xs font-semibold border flex items-center gap-1.5 transition-all ${
    on ? "bg-sky-600/20 border-sky-500/50 text-sky-200" : "bg-black/30 border-white/10 text-slate-400 hover:text-white"}`;

  return (
    <div data-testid="tgc-view" className="space-y-6">
      {/* Conexión */}
      <div className="bg-gradient-to-br from-[#12141D] to-[#0E2233] border border-sky-500/30 rounded-2xl p-6 shadow-2xl space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-sky-500/15 border border-sky-500/40 text-sky-300 flex items-center justify-center shrink-0">
              <Send className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-lg font-black text-white">Canal de Telegram</h3>
                <span className="text-[10px] uppercase font-mono font-bold px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-300 border border-sky-500/30">Solo admin</span>
                {status.connected ? (
                  <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/40">@{status.channel}</span>
                ) : (
                  <span className="text-[10px] uppercase font-mono font-bold px-2 py-0.5 rounded-full bg-slate-800 text-slate-400">Sin conectar</span>
                )}
              </div>
              <p className="text-xs text-slate-400 mt-0.5 max-w-2xl">
                Un canal público propio que publica vídeos a horas fijas, con botones a tus webs y juegos. Va aparte de los juegos y de @arcadegameclub.
              </p>
            </div>
          </div>
          {status.connected && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] text-slate-400 font-mono">Vídeos en 24 h: {status.posts_today}/{status.max_posts}</span>
              <Button variant="ghost" size="sm" onClick={disconnect} disabled={working !== null}
                className="h-8 text-xs text-slate-400 hover:text-red-300 flex items-center gap-1">
                <Unplug className="w-3.5 h-3.5" /> Desconectar
              </Button>
            </div>
          )}
        </div>

        {feedback && (
          <div className={`p-3 rounded-xl text-xs flex items-start gap-2 break-words ${fbStyle}`}>
            {feedback.type === "success" ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />}
            <span className="min-w-0">{feedback.text}</span>
          </div>
        )}

        {!status.connected && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="p-4 rounded-xl bg-black/40 border border-white/5 text-xs text-slate-300 space-y-2 min-w-0">
              <p className="font-bold text-white flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5 text-sky-300" /> Bot y canal</p>
              <ol className="list-decimal list-inside space-y-1 text-slate-400">
                <li>En Telegram crea un <strong className="text-slate-200">canal público</strong> nuevo, con su @nombre.</li>
                <li>Habla con <strong className="text-slate-200">@BotFather</strong>, usa /newbot y copia el token. No lo compartas con nadie, tampoco en un chat.</li>
                <li>Añade ese bot al canal como <strong className="text-slate-200">administrador</strong> con permiso para publicar.</li>
                <li>Pega aquí el canal y el token.</li>
              </ol>
            </div>
            <div className="space-y-2">
              <input type="text" placeholder="@tucanal" value={form.channel} autoComplete="off"
                onChange={(e) => setForm((f) => ({ ...f, channel: e.target.value }))} className={input} />
              <input type="password" placeholder={status.has_token ? "Token guardado (déjalo vacío para usarlo)" : "Token del bot"} value={form.bot_token} autoComplete="new-password"
                onChange={(e) => setForm((f) => ({ ...f, bot_token: e.target.value }))} className={input} />
              <Button data-testid="tgc-connect-btn" onClick={connect} disabled={working !== null || !form.channel || (!form.bot_token && !status.has_token)}
                className="w-full bg-sky-600 hover:bg-sky-500 text-white font-black text-xs flex items-center justify-center gap-1.5">
                {working === "connect" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
                Conectar canal
              </Button>
              <p className="text-[10px] text-slate-500">El token se guarda solo en el servidor y nunca se devuelve al navegador.</p>
              <p className="text-[11px] text-slate-500">Sin conectar también puedes crear vídeos: se quedan aquí para verlos.</p>
            </div>
          </div>
        )}
      </div>

      {/* Crecimiento */}
      {status.connected && (
        <div className={card}>
          <h4 className="text-sm font-black text-white flex items-center gap-1.5"><Users className="w-4 h-4 text-sky-300" /> Camino a los anuncios del canal</h4>
          <div className="flex items-baseline justify-between text-xs text-slate-300">
            <span><strong className="text-white text-lg">{members.toLocaleString("es-ES")}</strong> suscriptores</span>
            <span>Objetivo: {status.goal.toLocaleString("es-ES")}</span>
          </div>
          <div className="h-2 rounded-full bg-black/50 overflow-hidden">
            <div className="h-full bg-sky-500" style={{ width: `${pct}%` }} />
          </div>
          <p className="text-[11px] text-slate-500">
            {stats ? `${stats.published} vídeos publicados. ` : ""}
            Telegram no da las visualizaciones a los bots: se ven debajo de cada vídeo en el canal. Nunca premies suscribirse: Telegram puede dejarte fuera de los anuncios.
          </p>
        </div>
      )}

      {/* Crear ahora */}
      <div className={card}>
        <h4 className="text-sm font-black text-white flex items-center gap-1.5"><Film className="w-4 h-4 text-sky-300" /> Crear un vídeo ahora</h4>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => createVideo("radar")} disabled={working !== null || busy || !config.radar_fresh}
            title={config.radar_fresh ? "" : "El Radar Viral no tiene temas nuevos de las últimas 72 h"}
            className="bg-amber-600 hover:bg-amber-500 text-white font-bold text-xs flex items-center gap-1.5">
            <RadarIcon className="w-3.5 h-3.5" /> Radar Viral: un tema ({config.radar_fresh})
          </Button>
          <Button onClick={() => createVideo("comparacesta", "producto")} disabled={working !== null || busy}
            className="bg-emerald-700 hover:bg-emerald-600 text-white font-bold text-xs flex items-center gap-1.5">
            <ShoppingCart className="w-3.5 h-3.5" /> ComparaCesta: un producto
          </Button>
          <Button onClick={() => createVideo("comparacesta", "cesta")} disabled={working !== null || busy}
            className="bg-emerald-800 hover:bg-emerald-700 text-white font-bold text-xs flex items-center gap-1.5">
            <ShoppingCart className="w-3.5 h-3.5" /> ComparaCesta: una cesta
          </Button>
          <Button onClick={() => createVideo("prisa")} disabled={working !== null || busy}
            className="bg-orange-600 hover:bg-orange-500 text-white font-bold text-xs flex items-center gap-1.5">
            <Zap className="w-3.5 h-3.5" /> Prisa o Ahorro: un chollo
          </Button>
          <div className="flex items-center gap-1.5">
            <select value={game} onChange={(e) => setGame(e.target.value)}
              className="bg-black/40 border border-white/10 rounded-lg px-2 py-2 text-xs text-white">
              <option value="">Juego (el siguiente)</option>
              {gamesWithMaterial.map(([g, m]) => <option key={g} value={g}>{m.name}</option>)}
            </select>
            <Button onClick={() => createVideo("juegos", game || undefined)} disabled={working !== null || busy || gamesWithMaterial.length === 0}
              className="bg-violet-600 hover:bg-violet-500 text-white font-bold text-xs flex items-center gap-1.5">
              <Gamepad2 className="w-3.5 h-3.5" /> Clip de juego
            </Button>
          </div>
        </div>
        <p className="text-[10px] text-slate-500">
          Se hace uno a la vez. Los temas del Radar se leen sin tocarlos y la IA escribe un guion nuevo. El material de los juegos es el de la pestaña TikTok.
          {busy && " Ahora mismo se está creando uno."}
        </p>
      </div>

      {/* Piloto */}
      <div className={card}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <h4 className="text-sm font-black text-white flex items-center gap-1.5"><Bot className="w-4 h-4 text-sky-300" /> Piloto automático</h4>
          <button onClick={() => saveConfig({ autopilot_enabled: !s.autopilot_enabled })} disabled={working !== null}
            className={`px-3 py-1.5 rounded-lg text-xs font-black border ${s.autopilot_enabled
              ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-300" : "bg-slate-800 border-white/10 text-slate-400"}`}>
            {s.autopilot_enabled ? "ENCENDIDO" : "APAGADO"}
          </button>
        </div>
        <p className="text-xs text-slate-400">
          Publica a las horas elegidas (hora de España), turnándose entre las fuentes activadas.
          {config.next_post && <> Próximo: <strong className="text-slate-200">{new Date(config.next_post).toLocaleString("es-ES")}</strong>.</>}
        </p>
        {s.last_error && <p className="text-[11px] text-red-400 break-words">{s.last_error}</p>}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-xs">
          <div className="space-y-2">
            <p className="text-slate-300 font-bold">Horas de publicación (máximo {config.max_posts})</p>
            <div className="flex flex-wrap gap-1.5">
              {HOURS.map((h) => (
                <button key={h} onClick={() => saveConfig({ post_hours: toggleIn(s.post_hours || [], h) })} disabled={working !== null} className={chip((s.post_hours || []).includes(h))}>{h}:00</button>
              ))}
            </div>
            <p className="text-slate-300 font-bold pt-1">Fuentes</p>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(config.source_names).map(([k, label]) => {
                const Icon = SOURCE_ICON[k] || Film;
                return (
                  <button key={k} onClick={() => saveConfig({ sources: toggleIn(s.sources || [], k) })} disabled={working !== null} className={chip((s.sources || []).includes(k))}>
                    <Icon className="w-3.5 h-3.5" /> {label}
                  </button>
                );
              })}
            </div>
            <p className="text-slate-300 font-bold pt-1">Juegos</p>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(config.games).map(([g, info]) => (
                <button key={g} onClick={() => saveConfig({ games: toggleIn(s.games || [], g) })} disabled={working !== null} className={chip((s.games || []).includes(g))}>
                  {info.name}{info.ok ? "" : " (sin material)"}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-slate-300">
              <input type="checkbox" checked={!!s.auto_publish} onChange={(e) => saveConfig({ auto_publish: e.target.checked })} className="accent-sky-500 w-4 h-4" />
              Publicar sin revisar (si no, espera a que pulses Publicar)
            </label>
            <label className="flex items-center gap-2 text-slate-300">
              <input type="checkbox" checked={!!s.voice_enabled} onChange={(e) => saveConfig({ voice_enabled: e.target.checked })} className="accent-sky-500 w-4 h-4" />
              Voz en off en los vídeos del Radar
            </label>
            <label className="block text-slate-300 font-bold pt-1">Código postal de ComparaCesta (opcional)</label>
            <input type="text" inputMode="numeric" maxLength={5} placeholder="Sin código: precios online generales" defaultValue={s.cc_postal_code || ""}
              onBlur={(e) => e.target.value !== (s.cc_postal_code || "") && saveConfig({ cc_postal_code: e.target.value })} className={input} />
          </div>
        </div>
      </div>

      {/* Lista */}
      <div className={card}>
        <div className="flex items-center justify-between gap-2">
          <h4 className="text-sm font-black text-white flex items-center gap-1.5"><Send className="w-4 h-4 text-sky-300" /> Vídeos ({videos.length})</h4>
          <Button variant="ghost" size="sm" onClick={() => { loadVideos(); loadStatus(); loadStats(); }} className="h-8 text-xs text-slate-400 hover:text-white flex items-center gap-1">
            <RefreshCw className="w-3.5 h-3.5" /> Actualizar
          </Button>
        </div>
        {videos.length === 0 && <p className="text-xs text-slate-500">Todavía no hay ninguno.</p>}
        <div className="space-y-2">
          {videos.map((v) => {
            const st = STATUS[v.status] || STATUS.failed;
            const Icon = SOURCE_ICON[v.source] || Film;
            return (
              <div key={v.id} data-testid="tgc-video-row" className="p-3 rounded-xl bg-black/30 border border-white/5 space-y-2">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-white flex items-center gap-1.5 min-w-0">
                      <Icon className="w-3.5 h-3.5 text-sky-300 shrink-0" /><span className="truncate">{v.title}</span>
                    </p>
                    <p className="text-[11px] text-slate-500 break-words">{v.progress}{v.voice ? " · con voz" : ""}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                    <span className={`text-[10px] font-bold px-2 py-1 rounded-full border ${st.style}`}>{st.label}</span>
                    {v.video_url && !v.video_deleted && (
                      <Button size="sm" variant="ghost" onClick={() => setPreview(preview === v.id ? null : v.id)} className="h-7 px-2 text-[11px] text-slate-300 hover:text-white flex items-center gap-1">
                        <PlayCircle className="w-3.5 h-3.5" /> Ver
                      </Button>
                    )}
                    {v.video_url && !v.video_deleted && v.whatsapp_text && (
                      <Button data-testid="tgc-whatsapp-btn" size="sm" variant="ghost" onClick={() => prepareWhatsApp(v)} disabled={working !== null}
                        className="h-7 px-2 text-[11px] text-green-300 hover:text-white flex items-center gap-1">
                        {working === `wa-${v.id}` ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <MessageCircle className="w-3.5 h-3.5" />}
                        Preparar para WhatsApp
                      </Button>
                    )}
                    {v.status === "ready" && status.connected && (
                      <Button size="sm" onClick={() => publishVideo(v)} disabled={working !== null}
                        className="h-7 px-2 bg-sky-600 hover:bg-sky-500 text-white text-[11px] font-bold flex items-center gap-1">
                        <Upload className="w-3.5 h-3.5" /> Publicar
                      </Button>
                    )}
                    {v.post_url && (
                      <a href={v.post_url} target="_blank" rel="noopener noreferrer" className="h-7 px-2 text-[11px] text-emerald-300 hover:text-white flex items-center gap-1">
                        <ExternalLink className="w-3.5 h-3.5" /> Abrir
                      </a>
                    )}
                    {!st.busy && (
                      <Button size="sm" variant="ghost" onClick={() => deleteVideo(v)} disabled={working !== null} className="h-7 px-2 text-slate-500 hover:text-red-300">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
                {v.error && v.status !== "failed" && <p className="text-[11px] text-amber-300 break-words">{v.error}</p>}
                {preview === v.id && <ChannelPreview video={v} />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// El vídeo va con token en la cabecera, así que se baja como blob para enseñarlo
const ChannelPreview = ({ video }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    axios.get(`${BACKEND_URL}${video.video_url}`, { ...opts(), responseType: "blob" }).then((res) => {
      url = URL.createObjectURL(res.data);
      setSrc(url);
    }).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [video.video_url]);
  return (
    <div className="flex flex-col sm:flex-row gap-3">
      {src ? <video src={src} controls className="w-40 rounded-lg border border-white/10 shrink-0" /> : <p className="text-xs text-slate-500">Cargando vídeo…</p>}
      <div className="text-xs text-slate-300 min-w-0 space-y-1">
        <p className="font-bold text-white break-words">{video.post?.title}</p>
        <p className="whitespace-pre-wrap break-words text-slate-400">{video.post?.body}</p>
        {(video.post?.buttons || []).map((b) => <p key={b.url} className="break-all text-sky-200">{b.text}</p>)}
        {video.whatsapp_text && (
          <div className="pt-2 space-y-1">
            <p className="font-bold text-green-300">Texto para WhatsApp</p>
            <p data-testid="tgc-whatsapp-text" className="whitespace-pre-wrap break-words text-slate-400">{video.whatsapp_text}</p>
          </div>
        )}
      </div>
    </div>
  );
};
